/**
 * apps/web :: lib/checkout-api
 *
 * Typed wrappers around the `/api/checkout/*` surface (FE-02 + TAX-01,
 * ROADS-445). Field names mirror the backend wire shape verbatim
 * (`@SerialName` snake_case) — same convention as the rest of the
 * frontend's API libs.
 *
 * Endpoints:
 *   - POST /api/checkout/session  → 200 {checkout_url, session_id}
 *                                    400 validation_failed (tier / shape)
 *                                    401 unauthenticated
 *                                    503 checkout_unavailable
 *
 * Auth: requires the `rb_session` cookie; `apiFetch` always sends
 * credentials. 401s bubble up as `ApiError` and `ProtectedRoute`
 * handles the bounce to /login at the parent route.
 */
import { apiFetch } from "@/lib/api";
import type { EntitlementTier } from "@/lib/entitlement-api";

/** Request body shape for the create-session POST. */
export interface CreateCheckoutSessionRequest {
  tier: EntitlementTier;
  state_code?: string | null;
  country_code?: string | null;
  /** Optional override; production FE never sends these. */
  success_url?: string;
  cancel_url?: string;
  /**
   * FE-04 (ROADS-445) tourist -> resident upgrade. Optional id of an
   * active tourist entitlement to credit forward into the new tier.
   * The backend validates ownership + tier + activity + source. A 400
   * means the upgrade is invalid; a 403 means the entitlement belongs
   * to someone else; in either case the FE can fall back to a no-credit
   * purchase by retrying without this field.
   */
  upgrade_from_entitlement_id?: string;
}

/** Wire form for the FE-04 `credit_method` field on the response. */
export type CheckoutCreditMethod = "expiry_extension" | "stripe_discount";

/** Response body for the success path. */
export interface CreateCheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
  /**
   * FE-04 upgrade fields. Present only when the request carried
   * `upgrade_from_entitlement_id` AND the backend's eligibility checks
   * passed. Surfaced to the user on /checkout/success as a "Credit of
   * $X applied" line.
   */
  credit_applied_cents?: number;
  credit_method?: CheckoutCreditMethod;
  upgrade_bonus_days?: number;
  upgrade_from_entitlement_id?: string;
}

/**
 * Create a Stripe Checkout session for the authenticated user. The
 * caller redirects the browser to `checkout_url` via
 * `window.location.href` to hand off to Stripe's hosted Checkout
 * page.
 *
 * Throws `ApiError` on any non-2xx; the page-level handler maps:
 *   - status 400 → "There's a problem with your selection — pick a
 *                  state below and try again" (validation feedback).
 *   - status 401 → ProtectedRoute bounces to /login on next render.
 *   - status 503 → "Checkout is temporarily unavailable. Please try
 *                  again in a minute."
 */
export async function createCheckoutSession(
  body: CreateCheckoutSessionRequest,
): Promise<CreateCheckoutSessionResponse> {
  const wireBody: Record<string, unknown> = { tier: body.tier };
  if (body.state_code) wireBody.state_code = body.state_code;
  if (body.country_code) wireBody.country_code = body.country_code;
  if (body.success_url) wireBody.success_url = body.success_url;
  if (body.cancel_url) wireBody.cancel_url = body.cancel_url;
  if (body.upgrade_from_entitlement_id) {
    wireBody.upgrade_from_entitlement_id = body.upgrade_from_entitlement_id;
  }
  return apiFetch<CreateCheckoutSessionResponse>("/api/checkout/session", {
    method: "POST",
    body: JSON.stringify(wireBody),
  });
}
